import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  Badge,
  List,
  Divider,
  Icon,
  Checkbox,
  Select,
  FormLayout
} from '@shopify/polaris';
import { 
  NotificationMajor,
  EmailMajor,
  SettingsMajor
} from '@shopify/polaris-icons';
import { useAuth } from '../hooks/useAuth';

interface NotificationSettings {
  jobStarted: boolean;
  jobCompleted: boolean;
  jobFailed: boolean;
  welcomeEmail: boolean;
  emailFrequency: 'immediate' | 'daily' | 'weekly';
  emailAddress: string;
}

const EMAIL_FREQUENCY_OPTIONS = [
  { label: 'Immediate', value: 'immediate' },
  { label: 'Daily Digest', value: 'daily' },
  { label: 'Weekly Summary', value: 'weekly' }
];

export const Notifications: React.FC = () => {
  const { user: _user } = useAuth();
  const [settings, setSettings] = useState<NotificationSettings>({
    jobStarted: true,
    jobCompleted: true,
    jobFailed: true,
    welcomeEmail: true,
    emailFrequency: 'immediate',
    emailAddress: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    loadNotificationSettings();
  }, []);

  const loadNotificationSettings = async () => {
    try {
      // TODO: Replace with actual API call
      // const response = await apiService.getNotificationSettings();
      // setSettings(response.data);
      
      // Mock data for now
      setSettings({
        jobStarted: true,
        jobCompleted: true,
        jobFailed: true,
        welcomeEmail: true,
        emailFrequency: 'immediate',
        emailAddress: 'user@example.com'
      });
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    }
  };

  const saveNotificationSettings = async () => {
    setIsSaving(true);
    try {
      // TODO: Replace with actual API call
      // await apiService.updateNotificationSettings(settings);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save notification settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSettingChange = (key: keyof NotificationSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const testEmailNotification = async () => {
    try {
      // TODO: Replace with actual API call
      // await apiService.sendTestEmail();
    } catch (error) {
      console.error('Failed to send test email:', error);
    }
  };

  return (
    <Page title="Notifications">
      <Layout>
        <Layout.Section>
          {showSuccess && (
            <Banner tone="success" onDismiss={() => setShowSuccess(false)}>
              Notification settings saved successfully!
            </Banner>
          )}

          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <Icon source={NotificationMajor} />
                <div style={{ marginLeft: '12px' }}>
                  <Text variant="headingMd" as="h2">
                    Email Notifications
                  </Text>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Manage your email notification preferences for translation jobs.
                  </Text>
                </div>
              </div>

              <FormLayout>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="headingSm" as="h3">
                      Notification Types
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Checkbox
                      label="Job Started"
                      checked={settings.jobStarted}
                      onChange={(checked) => handleSettingChange('jobStarted', checked)}
                      helpText="Receive an email when a translation job starts processing"
                    />
                    
                    <Checkbox
                      label="Job Completed"
                      checked={settings.jobCompleted}
                      onChange={(checked) => handleSettingChange('jobCompleted', checked)}
                      helpText="Receive an email when a translation job completes successfully"
                    />
                    
                    <Checkbox
                      label="Job Failed"
                      checked={settings.jobFailed}
                      onChange={(checked) => handleSettingChange('jobFailed', checked)}
                      helpText="Receive an email when a translation job fails"
                    />
                    
                    <Checkbox
                      label="Welcome Email"
                      checked={settings.welcomeEmail}
                      onChange={(checked) => handleSettingChange('welcomeEmail', checked)}
                      helpText="Receive a welcome email when you first sign up"
                    />
                  </div>
                </div>

                <Divider />

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <Text variant="headingSm" as="h3">
                      Email Preferences
                    </Text>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Email Address
                        </Text>
                      </div>
                      <Text variant="bodyMd" as="p" tone="subdued">
                        {settings.emailAddress || 'No email address set'}
                      </Text>
                    </div>
                    
                    <div>
                      <div style={{ marginBottom: '8px' }}>
                        <Text variant="bodyMd" as="p" fontWeight="semibold">
                          Notification Frequency
                        </Text>
                      </div>
                      <Select
                        label=""
                        options={EMAIL_FREQUENCY_OPTIONS}
                        value={settings.emailFrequency}
                        onChange={(value) => handleSettingChange('emailFrequency', value)}
                        helpText="Choose how often you want to receive notifications"
                      />
                    </div>
                  </div>
                </div>

                <Divider />

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <Button 
                    variant="primary" 
                    onClick={saveNotificationSettings}
                    loading={isSaving}
                  >
                    Save Settings
                  </Button>
                  <Button 
                    onClick={testEmailNotification}
                    disabled={!settings.jobCompleted}
                  >
                    Send Test Email
                  </Button>
                </div>
              </FormLayout>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <Icon source={EmailMajor} />
                <div style={{ marginLeft: '12px' }}>
                  <Text variant="headingMd" as="h2">
                    Email Templates
                  </Text>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Preview the email notifications you'll receive.
                  </Text>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ 
                  border: '1px solid #e1e3e5', 
                  borderRadius: '6px', 
                  padding: '16px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text variant="headingSm" as="h3">Job Completed</Text>
                    <Badge tone="success">Active</Badge>
                  </div>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Professional email with job details, statistics, and next steps.
                  </Text>
                </div>

                <div style={{ 
                  border: '1px solid #e1e3e5', 
                  borderRadius: '6px', 
                  padding: '16px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text variant="headingSm" as="h3">Job Failed</Text>
                    <Badge tone="critical">Active</Badge>
                  </div>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Error details with troubleshooting steps and support contact.
                  </Text>
                </div>

                <div style={{ 
                  border: '1px solid #e1e3e5', 
                  borderRadius: '6px', 
                  padding: '16px',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <Text variant="headingSm" as="h3">Welcome Email</Text>
                    <Badge tone="success">Active</Badge>
                  </div>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Welcome message with getting started guide and feature overview.
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                <Icon source={SettingsMajor} />
                <div style={{ marginLeft: '12px' }}>
                  <Text variant="headingMd" as="h2">
                    Notification Features
                  </Text>
                  <Text variant="bodyMd" as="p" tone="subdued">
                    What you can expect from our email notification system.
                  </Text>
                </div>
              </div>

              <List type="bullet">
                <List.Item>
                  <Text variant="bodyMd" as="p">
                    <strong>Real-time Updates:</strong> Get notified immediately when jobs start, complete, or fail
                  </Text>
                </List.Item>
                <List.Item>
                  <Text variant="bodyMd" as="p">
                    <strong>Detailed Information:</strong> Each email includes job details, statistics, and actionable next steps
                  </Text>
                </List.Item>
                <List.Item>
                  <Text variant="bodyMd" as="p">
                    <strong>Professional Design:</strong> Beautiful, responsive email templates that work on all devices
                  </Text>
                </List.Item>
                <List.Item>
                  <Text variant="bodyMd" as="p">
                    <strong>Error Handling:</strong> Failed job emails include troubleshooting steps and support contact
                  </Text>
                </List.Item>
                <List.Item>
                  <Text variant="bodyMd" as="p">
                    <strong>Customizable:</strong> Choose which notifications you want to receive and how often
                  </Text>
                </List.Item>
              </List>
            </div>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}; 